import * as React from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Chip,
  CircularProgress,
  Alert,
  Rating,
} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import AdminShell, {
  fieldSx,
  panelSx,
  primaryButtonSx,
  selectMenuProps,
  manageButtonSx,
  tileHeaderSx,
  infoBlockSx,
  mutedTextSx,
} from "./AdminShell";
import { EMPTY_OPTION, OptionSummary, ratingCriteria, toOptionForm, type OptionForm } from "./payloadOptions";
import { getSession } from "../../api/session";
import { slugify } from "../utils/slug";
import {
  adminListAllMissions,
  fetchPayloadCategories,
  fetchPayloadOptions,
  fetchMissionPayloadAssignments,
  adminUpsertPayloadCategory,
  adminDeletePayloadCategory,
  adminUpsertPayloadOption,
  adminDeletePayloadOption,
  adminSetPayloadOptionMission,
  uploadPayloadOptionImage,
  uploadPayloadSpecSheet,
} from "../../api/queries";
import type { AdminMissionRow } from "../../types/admin";
import type { PayloadCategoryRow, PayloadOptionRow } from "../../types/payload";

// Picked in the Type dropdown to create a type on the spot.
const NEW_TYPE = "__new__";

// A payload as edited in the form: its own fields, its type (an existing one
// or a new one typed in), and the missions it is offered in.
type PayloadForm = OptionForm & {
  categoryId: string;
  newTypeLabel: string;
  newTypeCriterion: string;
  missionIds: string[];
};

const EMPTY_PAYLOAD: PayloadForm = {
  ...EMPTY_OPTION,
  categoryId: "",
  newTypeLabel: "",
  newTypeCriterion: "",
  missionIds: [],
};

const EMPTY_TYPE_FORM = { id: null as string | null, label: "", customCriterionLabel: "", orderIndex: 0 };

const smallPrimaryButtonSx = primaryButtonSx({
  fontSize: 13,
  color: "#FFFFFF",
  "&.Mui-disabled": { bgcolor: "rgba(124,58,237,0.25)", color: "rgba(255,255,255,0.4)" },
});

const textButtonSx = { textTransform: "none", fontWeight: 700, fontSize: 13, color: "rgba(226,232,240,0.7)" };

const missionChipSx = { bgcolor: "rgba(168,85,247,0.18)", color: "#E9D5FF", fontSize: 11 };

const formHeadingSx = { color: "#E9D5FF", fontWeight: 800, fontSize: 12.5, letterSpacing: "0.08em", textTransform: "uppercase" };

export default function AdminPayloads(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;
  const session = React.useMemo(() => getSession(), []);
  const email = session?.email ?? "";

  const [types, setTypes] = React.useState<PayloadCategoryRow[]>([]);
  const [payloads, setPayloads] = React.useState<PayloadOptionRow[]>([]);
  const [assignments, setAssignments] = React.useState<{ mission_id: string; option_id: string }[]>([]);
  const [missions, setMissions] = React.useState<AdminMissionRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [uploadingImage, setUploadingImage] = React.useState(false);
  const [uploadingSheet, setUploadingSheet] = React.useState(false);

  const [showAddForm, setShowAddForm] = React.useState(false);
  const [addForm, setAddForm] = React.useState(EMPTY_PAYLOAD);

  // One payload tile is open at a time: "view" (clicking the tile) is
  // read-only, "manage" (the Manage button) edits it and its missions.
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [mode, setMode] = React.useState<"view" | "manage">("view");
  const [editForm, setEditForm] = React.useState(EMPTY_PAYLOAD);

  // Renaming a type (its pen), inline in the type's heading.
  const [typeForm, setTypeForm] = React.useState(EMPTY_TYPE_FORM);

  const load = React.useCallback(
    async (showSpinner: boolean) => {
      if (showSpinner) setLoading(true);
      const [typesRes, payloadsRes, assignmentsRes, missionsRes] = await Promise.all([
        fetchPayloadCategories(),
        fetchPayloadOptions(),
        fetchMissionPayloadAssignments(),
        adminListAllMissions(email),
      ]);
      setLoading(false);
      const loadError = typesRes.error ?? payloadsRes.error ?? assignmentsRes.error ?? missionsRes.error;
      if (loadError) {
        setError(loadError.message);
        return;
      }
      setTypes(typesRes.data ?? []);
      setPayloads(payloadsRes.data ?? []);
      setAssignments(assignmentsRes.data ?? []);
      setMissions((missionsRes.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)));
    },
    [email]
  );

  React.useEffect(() => {
    load(true);
  }, [load]);

  const missionIdsOf = React.useCallback(
    (payloadId: string) => assignments.filter((a) => a.option_id === payloadId).map((a) => a.mission_id),
    [assignments]
  );

  const typeById = React.useMemo(() => new Map(types.map((t) => [t.id, t])), [types]);

  // The type a form currently points at: an existing one, or the one being typed in.
  const typeOfForm = (form: PayloadForm) =>
    form.categoryId === NEW_TYPE
      ? { label: form.newTypeLabel.trim(), criterion: form.newTypeCriterion.trim() }
      : { label: typeById.get(form.categoryId)?.label ?? "", criterion: typeById.get(form.categoryId)?.custom_criterion_label ?? "" };

  const openPayload = (payload: PayloadOptionRow, nextMode: "view" | "manage") => {
    const closing = selectedId === payload.id && mode === nextMode;
    setSelectedId(closing ? null : payload.id);
    setMode(nextMode);
    setError(null);
    setEditForm(
      closing
        ? EMPTY_PAYLOAD
        : { ...EMPTY_PAYLOAD, ...toOptionForm(payload), categoryId: payload.category_id, missionIds: missionIdsOf(payload.id) }
    );
  };

  // Saves a payload: creates its type first if a new one was typed in, then
  // the payload, then offers / stops offering it mission by mission.
  const savePayload = async (form: PayloadForm) => {
    const type = typeOfForm(form);
    if (!form.name.trim()) {
      setError("The payload needs a name.");
      return;
    }
    if (!type.label) {
      setError("Choose the payload's type, or enter a name for the new type.");
      return;
    }

    setSaving(true);
    setError(null);
    const fail = (message: string) => {
      setSaving(false);
      setError(message);
      // Part of the save may already have gone through; show what is stored.
      load(false);
    };

    let categoryId = form.categoryId;
    if (categoryId === NEW_TYPE) {
      const { data: created, error: typeError } = await adminUpsertPayloadCategory(email, {
        id: null,
        label: type.label,
        customCriterionLabel: type.criterion,
        orderIndex: types.length,
      });
      if (typeError || !created) return fail(typeError?.message ?? "Couldn't create the payload type.");
      categoryId = created.id;
    }

    const { data: saved, error: saveError } = await adminUpsertPayloadOption(email, {
      id: form.id,
      categoryId,
      name: form.name.trim(),
      manufacturer: form.manufacturer.trim(),
      summary: form.summary.trim(),
      imageUrl: form.imageUrl.trim() || null,
      specSheetUrl: form.specSheetUrl.trim() || null,
      ratingResolution: form.ratingResolution,
      ratingEnergy: form.ratingEnergy,
      ratingMass: form.ratingMass,
      ratingCost: form.ratingCost,
      ratingTrl: form.ratingTrl,
      orderIndex: form.orderIndex,
    });
    if (saveError || !saved) return fail(saveError?.message ?? "Couldn't save the payload.");

    const before = form.id ? missionIdsOf(form.id) : [];
    const changes = [
      ...form.missionIds.filter((id) => !before.includes(id)).map((id) => ({ missionId: id, assigned: true })),
      ...before.filter((id) => !form.missionIds.includes(id)).map((id) => ({ missionId: id, assigned: false })),
    ];
    for (const change of changes) {
      const { error: assignError } = await adminSetPayloadOptionMission(email, saved.id, change.missionId, change.assigned);
      if (assignError) return fail(assignError.message);
    }

    setSaving(false);
    if (form.id === null) {
      setAddForm(EMPTY_PAYLOAD);
      setShowAddForm(false);
    } else {
      // A type created while editing is now a real one; point the form at it.
      setEditForm((prev) => ({ ...prev, categoryId, newTypeLabel: "", newTypeCriterion: "" }));
    }
    load(false);
  };

  const deletePayload = async (payload: PayloadOptionRow) => {
    const inMissions = missionIdsOf(payload.id).length;
    const warning = inMissions
      ? ` It is removed from ${inMissions} ${inMissions === 1 ? "mission" : "missions"}, and groups that selected it lose that selection.`
      : "";
    if (!window.confirm(`Delete "${payload.name}"?${warning}`)) return;

    const { error: delError } = await adminDeletePayloadOption(email, payload.id);
    if (delError) {
      setError(delError.message);
      return;
    }
    if (selectedId === payload.id) setSelectedId(null);
    load(false);
  };

  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    form: PayloadForm,
    setForm: React.Dispatch<React.SetStateAction<PayloadForm>>
  ) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!["image/jpeg", "image/jpg", "image/png"].includes(file.type)) {
      setError("Only JPEG, JPG, or PNG image files are allowed.");
      return;
    }
    // The file is stored as "<name>_<manufacturer>" in the type's folder.
    const type = typeOfForm(form);
    if (!type.label || !form.name.trim()) {
      setError("Choose the type and enter the payload's name (and manufacturer) before uploading its image.");
      return;
    }

    setUploadingImage(true);
    setError(null);

    const { data: publicUrl, error: uploadError } = await uploadPayloadOptionImage(
      { typeSlug: slugify(type.label), nameSlug: slugify(form.name), manufacturerSlug: slugify(form.manufacturer) },
      file
    );

    setUploadingImage(false);
    if (uploadError) {
      setError(uploadError.message);
      return;
    }
    setForm((prev) => ({ ...prev, imageUrl: publicUrl ?? "" }));
  };

  // For a payload whose spec sheet isn't online anywhere: the PDF goes into
  // the "payloads" bucket and its public URL fills the spec sheet URL field,
  // exactly as if it had been typed in. Stored with the payload on Save.
  const handleSheetUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    form: PayloadForm,
    setForm: React.Dispatch<React.SetStateAction<PayloadForm>>
  ) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (file.type !== "application/pdf") {
      setError("Only PDF files are allowed for the spec sheet.");
      return;
    }
    // The file is stored in a folder named after the payload.
    const payloadSlug = slugify(form.name);
    if (!payloadSlug) {
      setError("Enter the payload's name before uploading its spec sheet.");
      return;
    }

    setUploadingSheet(true);
    setError(null);

    const { data: publicUrl, error: uploadError } = await uploadPayloadSpecSheet(payloadSlug, file);

    setUploadingSheet(false);
    if (uploadError) {
      setError(uploadError.message);
      return;
    }
    setForm((prev) => ({ ...prev, specSheetUrl: publicUrl ?? "" }));
  };

  const saveType = async () => {
    if (!typeForm.id || !typeForm.label.trim()) return;
    const { error: saveError } = await adminUpsertPayloadCategory(email, {
      id: typeForm.id,
      label: typeForm.label.trim(),
      customCriterionLabel: typeForm.customCriterionLabel.trim(),
      orderIndex: typeForm.orderIndex,
    });
    if (saveError) {
      setError(saveError.message);
      return;
    }
    setTypeForm(EMPTY_TYPE_FORM);
    load(false);
  };

  // Only offered for a type with no payloads left, so nothing else goes with it.
  const deleteType = async (id: string) => {
    const { error: delError } = await adminDeletePayloadCategory(email, id);
    if (delError) {
      setError(delError.message);
      return;
    }
    load(false);
  };

  // The payload fields, shared by the "Add payload" panel and a tile's Manage view.
  const renderPayloadFields = (form: PayloadForm, setForm: React.Dispatch<React.SetStateAction<PayloadForm>>) => (
    <>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.25 }}>
        <TextField
          select
          label="Type"
          value={form.categoryId}
          onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
          sx={fieldSx}
          size="small"
          SelectProps={{ MenuProps: selectMenuProps }}
        >
          {types.map((t) => (
            <MenuItem key={t.id} value={t.id} sx={{ fontSize: 13 }}>
              {t.label}
            </MenuItem>
          ))}
          <MenuItem value={NEW_TYPE} sx={{ fontSize: 13, fontWeight: 700 }}>
            + New type…
          </MenuItem>
        </TextField>
        {form.categoryId === NEW_TYPE ? (
          <TextField
            label="New type's name (e.g. Camera)"
            size="small"
            value={form.newTypeLabel}
            onChange={(e) => setForm({ ...form, newTypeLabel: e.target.value })}
            sx={fieldSx}
          />
        ) : (
          <Box />
        )}
        {form.categoryId === NEW_TYPE && (
          <TextField
            label="New type's own rating criterion (e.g. Resolution [GSD])"
            size="small"
            value={form.newTypeCriterion}
            onChange={(e) => setForm({ ...form, newTypeCriterion: e.target.value })}
            sx={{ ...fieldSx, gridColumn: { sm: "1 / 3" } }}
          />
        )}
        <TextField label="Name" size="small" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} sx={fieldSx} />
        <TextField
          label="Manufacturer"
          size="small"
          value={form.manufacturer}
          onChange={(e) => setForm({ ...form, manufacturer: e.target.value })}
          sx={fieldSx}
        />
        <TextField label="Summary" size="small" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} sx={fieldSx} multiline />
        <Stack spacing={0.5}>
          <TextField
            label="Spec sheet URL"
            size="small"
            value={form.specSheetUrl}
            onChange={(e) => setForm({ ...form, specSheetUrl: e.target.value })}
            sx={fieldSx}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              component="label"
              size="small"
              disabled={uploadingSheet}
              startIcon={uploadingSheet ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <UploadRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: "none", fontWeight: 700, fontSize: 12.5, color: "#C4B5FD", border: "1px solid #4C3A70", borderRadius: 1.5, px: 1.25 }}
            >
              {uploadingSheet ? "Uploading…" : "Upload PDF instead"}
              <input type="file" hidden accept="application/pdf" onChange={(e) => handleSheetUpload(e, form, setForm)} />
            </Button>
            {form.specSheetUrl && (
              <Button size="small" onClick={() => setForm({ ...form, specSheetUrl: "" })} sx={textButtonSx}>
                Remove
              </Button>
            )}
          </Stack>
          <Typography sx={{ color: "rgba(226,232,240,0.5)", fontSize: 10.5 }}>
            Paste a link to the sheet, or upload the PDF and its link is filled in here. Enter the name first.
          </Typography>
        </Stack>
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              component="label"
              size="small"
              disabled={uploadingImage}
              startIcon={uploadingImage ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <UploadRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: "none", fontWeight: 700, fontSize: 12.5, color: "#C4B5FD", border: "1px solid #4C3A70", borderRadius: 1.5, px: 1.25 }}
            >
              {uploadingImage ? "Uploading…" : form.imageUrl ? "Replace image" : "Upload image"}
              <input type="file" hidden accept="image/jpeg,image/jpg,image/png" onChange={(e) => handleImageUpload(e, form, setForm)} />
            </Button>
            {form.imageUrl && (
              <Box
                component="img"
                src={form.imageUrl}
                sx={{ width: 32, height: 32, borderRadius: 1, objectFit: "cover", border: "1px solid #3E4258" }}
              />
            )}
            {form.imageUrl && (
              <Button size="small" onClick={() => setForm({ ...form, imageUrl: "" })} sx={textButtonSx}>
                Remove
              </Button>
            )}
          </Stack>
          <Typography sx={{ color: "rgba(226,232,240,0.5)", fontSize: 10.5 }}>
            JPEG, JPG or PNG. Choose the type and enter the name and manufacturer first.
          </Typography>
        </Stack>
      </Box>

      <Stack spacing={0.5}>
        <Typography sx={{ color: "rgba(226,232,240,0.75)", fontSize: 12, fontWeight: 700 }}>
          Ratings — click a star to set it, click it again to clear
        </Typography>
        {/* One criterion per line, stars lined up in their own column, so it is clear which stars belong to which label. */}
        <Box sx={{ display: "grid", gridTemplateColumns: "max-content max-content", columnGap: 2, rowGap: 0.5, alignItems: "center" }}>
          {ratingCriteria(typeOfForm(form).criterion).map((criterion) => (
            <React.Fragment key={criterion.key}>
              <Typography sx={{ fontSize: 12.5, color: "rgba(226,232,240,0.85)" }}>{criterion.label}</Typography>
              <Rating
                aria-label={criterion.label}
                max={5}
                value={form[criterion.key]}
                onChange={(_e, value) => setForm({ ...form, [criterion.key]: value ?? 0 })}
                icon={<StarRoundedIcon fontSize="inherit" />}
                emptyIcon={<StarBorderRoundedIcon fontSize="inherit" />}
                sx={{ fontSize: 26, color: "#FDE68A", "& .MuiRating-iconEmpty": { color: "rgba(226,232,240,0.35)" } }}
              />
            </React.Fragment>
          ))}
        </Box>
      </Stack>

      <Stack spacing={0.25}>
        <Typography sx={{ color: "rgba(226,232,240,0.75)", fontSize: 12, fontWeight: 700 }}>Offered in these missions</Typography>
        {missions.length === 0 && <Typography sx={mutedTextSx}>There are no missions yet. Create them on the Missions tab.</Typography>}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
          {missions.map((m) => (
            <FormControlLabel
              key={m.id}
              sx={{ mx: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={form.missionIds.includes(m.id)}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      missionIds: e.target.checked ? [...form.missionIds, m.id] : form.missionIds.filter((id) => id !== m.id),
                    })
                  }
                  sx={{ color: "rgba(196,181,253,0.6)", "&.Mui-checked": { color: "#A78BFA" } }}
                />
              }
              label={
                <Typography sx={{ fontSize: 13, color: "rgba(226,232,240,0.85)" }}>
                  {m.name}
                  {!m.is_active && " (inactive)"}
                </Typography>
              }
            />
          ))}
        </Box>
      </Stack>
    </>
  );

  const renderPayloadTile = (payload: PayloadOptionRow, type: PayloadCategoryRow) => {
    const open = selectedId === payload.id;
    const managing = open && mode === "manage";
    const payloadMissions = missions.filter((m) => missionIdsOf(payload.id).includes(m.id));

    return (
      <Box key={payload.id} sx={{ p: 1.5, borderRadius: 2, border: "1px solid rgba(168,85,247,0.25)", bgcolor: "rgba(255,255,255,0.02)" }}>
        <Stack spacing={1.5}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            rowGap={1}
            spacing={1}
            // While managing, a stray click on the header must not throw away unsaved edits.
            onClick={managing ? undefined : () => openPayload(payload, "view")}
            sx={managing ? undefined : tileHeaderSx}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              {open ? (
                <ExpandMoreRoundedIcon sx={{ fontSize: 20, color: "#C4B5FD" }} />
              ) : (
                <ChevronRightRoundedIcon sx={{ fontSize: 20, color: "rgba(226,232,240,0.6)" }} />
              )}
              {payload.image_url && (
                <Box
                  component="img"
                  src={payload.image_url}
                  sx={{ width: 36, height: 36, borderRadius: 1, objectFit: "cover", border: "1px solid #3E4258", flexShrink: 0 }}
                />
              )}
              <Stack spacing={0.4}>
                <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: 14 }}>
                  {payload.name}{" "}
                  {payload.manufacturer && (
                    <Box component="span" sx={{ color: "rgba(226,232,240,0.5)", fontWeight: 500, fontSize: 13 }}>({payload.manufacturer})</Box>
                  )}
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap rowGap={0.5}>
                  {payloadMissions.length === 0 && <Typography sx={mutedTextSx}>Not offered in any mission</Typography>}
                  {payloadMissions.map((m) => (
                    <Chip key={m.id} label={m.name} size="small" sx={missionChipSx} />
                  ))}
                </Stack>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openPayload(payload, "manage");
                }}
                startIcon={managing ? <CloseRoundedIcon sx={{ fontSize: 16 }} /> : <EditRoundedIcon sx={{ fontSize: 16 }} />}
                sx={{ ...manageButtonSx(managing), fontSize: 13, py: 0.4 }}
              >
                {managing ? "Close" : "Manage"}
              </Button>
              <IconButton
                size="small"
                aria-label="Delete payload"
                onClick={(e) => {
                  e.stopPropagation();
                  deletePayload(payload);
                }}
              >
                <DeleteOutlineRoundedIcon sx={{ fontSize: 17, color: "rgba(248,113,113,0.75)" }} />
              </IconButton>
            </Stack>
          </Stack>

          {open && !managing && (
            <Stack spacing={1} sx={{ pt: 1.25, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
              <OptionSummary option={payload} customCriterionLabel={type.custom_criterion_label} />
              {payload.summary && <Typography sx={{ color: "rgba(226,232,240,0.8)", fontSize: 13 }}>{payload.summary}</Typography>}
              {payload.spec_sheet_url && (
                <Typography sx={mutedTextSx}>
                  Spec sheet:{" "}
                  <Box component="a" href={payload.spec_sheet_url} target="_blank" rel="noopener noreferrer" sx={{ color: "#C4B5FD" }}>
                    {payload.spec_sheet_url}
                  </Box>
                </Typography>
              )}
            </Stack>
          )}

          {managing && (
            <Box sx={infoBlockSx}>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <EditRoundedIcon sx={{ fontSize: 15, color: "#C4B5FD" }} />
                  <Typography sx={formHeadingSx}>Payload info</Typography>
                </Stack>
                {renderPayloadFields(editForm, setEditForm)}
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button onClick={() => savePayload(editForm)} disabled={saving} sx={smallPrimaryButtonSx}>
                    {saving ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : "Save changes"}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}
        </Stack>
      </Box>
    );
  };

  return (
    <AdminShell title="Payload management" currentPage="AdminPayloads" handlePageChange={handlePageChange}>
      {error && (
        <Alert severity="error" variant="filled" onClose={() => setError(null)} sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {!showAddForm ? (
        <Button
          onClick={() => {
            // Most catalogues have few types; start on the first one.
            setAddForm({ ...EMPTY_PAYLOAD, categoryId: types[0]?.id ?? NEW_TYPE });
            setShowAddForm(true);
          }}
          startIcon={<AddRoundedIcon sx={{ fontSize: 18 }} />}
          sx={primaryButtonSx({ alignSelf: "flex-start" })}
        >
          Add new payload
        </Button>
      ) : (
        <Box sx={panelSx}>
          <Stack spacing={1.5}>
            <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: 15 }}>Add payload</Typography>
            {renderPayloadFields(addForm, setAddForm)}
            <Stack direction="row" spacing={1}>
              <Button onClick={() => savePayload(addForm)} disabled={saving} sx={primaryButtonSx()}>
                {saving ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : "Add payload"}
              </Button>
              <Button
                onClick={() => {
                  setAddForm(EMPTY_PAYLOAD);
                  setShowAddForm(false);
                }}
                sx={{ textTransform: "none", fontWeight: 700, color: "rgba(226,232,240,0.7)" }}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress sx={{ color: "#C4B5FD" }} />
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          {types.length === 0 && (
            <Typography sx={mutedTextSx}>No payloads yet. Add the first one, together with its type, above.</Typography>
          )}

          {types.map((type) => {
            const typePayloads = payloads.filter((p) => p.category_id === type.id);
            const renaming = typeForm.id === type.id;
            return (
              <Box key={type.id} sx={panelSx}>
                <Stack spacing={1.5}>
                  {renaming ? (
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr auto auto" }, gap: 1, alignItems: "center" }}>
                      <TextField
                        label="Type name (e.g. Camera)"
                        size="small"
                        value={typeForm.label}
                        onChange={(e) => setTypeForm({ ...typeForm, label: e.target.value })}
                        sx={fieldSx}
                      />
                      <TextField
                        label="Its own rating criterion (e.g. Resolution [GSD])"
                        size="small"
                        value={typeForm.customCriterionLabel}
                        onChange={(e) => setTypeForm({ ...typeForm, customCriterionLabel: e.target.value })}
                        sx={fieldSx}
                      />
                      <Button size="small" onClick={saveType} disabled={!typeForm.label.trim()} sx={smallPrimaryButtonSx}>
                        Save type
                      </Button>
                      <Button size="small" onClick={() => setTypeForm(EMPTY_TYPE_FORM)} sx={textButtonSx}>
                        Cancel
                      </Button>
                    </Box>
                  ) : (
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                      <Stack>
                        <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: 16 }}>{type.label}</Typography>
                        <Typography sx={mutedTextSx}>
                          {type.custom_criterion_label ? `Own criterion: ${type.custom_criterion_label} · ` : ""}
                          {typePayloads.length} {typePayloads.length === 1 ? "payload" : "payloads"}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5}>
                        <IconButton
                          size="small"
                          aria-label="Rename type"
                          title="Rename this type or change its own rating criterion"
                          onClick={() =>
                            setTypeForm({
                              id: type.id,
                              label: type.label,
                              customCriterionLabel: type.custom_criterion_label,
                              orderIndex: type.order_index,
                            })
                          }
                        >
                          <EditRoundedIcon sx={{ fontSize: 17, color: "rgba(226,232,240,0.6)" }} />
                        </IconButton>
                        {typePayloads.length === 0 && (
                          <IconButton size="small" aria-label="Delete type" title="Delete this empty type" onClick={() => deleteType(type.id)}>
                            <DeleteOutlineRoundedIcon sx={{ fontSize: 17, color: "rgba(248,113,113,0.75)" }} />
                          </IconButton>
                        )}
                      </Stack>
                    </Stack>
                  )}

                  {typePayloads.length === 0 && <Typography sx={mutedTextSx}>No payloads of this type.</Typography>}
                  {typePayloads.map((payload) => renderPayloadTile(payload, type))}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}
    </AdminShell>
  );
}
